import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api-fetch';

export type AudienceCandidate = {
  email: string;
  name: string;
  fileIndex?: number;
};

export type AudienceReviewRow = {
  email: string;
  name: string;
  sendable: boolean;
  blockReason?: string;
  contactedBefore: boolean;
  lastSentAt: string | null;
  lastOutcome: string | null;
  segment: 'new' | 'returning' | 'blocked';
};

type FilterKey = 'all' | 'new' | 'returning' | 'blocked' | 'sendable';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: AudienceCandidate[];
  siteId: string;
  onConfirm: (selected: AudienceCandidate[]) => void;
  confirming?: boolean;
};

export function SendAudienceReviewDialog({
  open,
  onOpenChange,
  candidates,
  siteId,
  onConfirm,
  confirming = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AudienceReviewRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<FilterKey>('all');

  const loadReview = useCallback(async () => {
    if (!open || candidates.length === 0) {
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch('/api/bulk-email/audience-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          recipients: candidates.map((c) => ({ email: c.email, name: c.name })),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Could not review recipients');
      }
      const reviewRows = (data.rows as AudienceReviewRow[]) || [];
      setRows(reviewRows);
      const safe = new Set(
        reviewRows.filter((r) => r.sendable).map((r) => r.email.toLowerCase())
      );
      setSelected(safe);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not review recipients');
      setRows([]);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, [open, candidates, siteId]);

  useEffect(() => {
    void loadReview();
  }, [loadReview]);

  const candidateByEmail = useMemo(() => {
    const map = new Map<string, AudienceCandidate>();
    for (const c of candidates) {
      map.set(c.email.toLowerCase(), c);
    }
    return map;
  }, [candidates]);

  const filteredRows = useMemo(() => {
    switch (filter) {
      case 'new':
        return rows.filter((r) => r.segment === 'new');
      case 'returning':
        return rows.filter((r) => r.segment === 'returning');
      case 'blocked':
        return rows.filter((r) => r.segment === 'blocked');
      case 'sendable':
        return rows.filter((r) => r.sendable);
      default:
        return rows;
    }
  }, [rows, filter]);

  const toggle = (email: string) => {
    const key = email.toLowerCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(filteredRows.map((r) => r.email.toLowerCase())));
  };

  const selectSendableOnly = () => {
    setSelected(new Set(rows.filter((r) => r.sendable).map((r) => r.email.toLowerCase())));
  };

  const selectNewOnly = () => {
    setSelected(
      new Set(rows.filter((r) => r.segment === 'new' && r.sendable).map((r) => r.email.toLowerCase()))
    );
  };

  const clearSelection = () => setSelected(new Set());

  const handleConfirm = () => {
    const chosen: AudienceCandidate[] = [];
    for (const email of selected) {
      const row = rows.find((r) => r.email.toLowerCase() === email);
      const base = candidateByEmail.get(email);
      if (!row?.sendable || !base) {
        continue;
      }
      chosen.push(base);
    }
    onConfirm(chosen);
  };

  const counts = {
    total: rows.length,
    new: rows.filter((r) => r.segment === 'new').length,
    returning: rows.filter((r) => r.segment === 'returning').length,
    blocked: rows.filter((r) => r.segment === 'blocked').length,
    sendable: rows.filter((r) => r.sendable).length,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl flex-col gap-4 overflow-hidden sm:w-full">
        <DialogHeader>
          <DialogTitle>Review recipients before sending</DialogTitle>
          <DialogDescription>
            We only recommend sending to working addresses. People who bounced or are blocked are
            turned off by default.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking your list…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="secondary">Total {counts.total}</Badge>
              <Badge variant="outline">New {counts.new}</Badge>
              <Badge variant="outline">Contacted before {counts.returning}</Badge>
              <Badge variant="outline">Blocked {counts.blocked}</Badge>
              <Badge>Ready to send {counts.sendable}</Badge>
            </div>

            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['all', 'All'],
                  ['new', 'New only'],
                  ['returning', 'Contacted before'],
                  ['blocked', 'Blocked / bounced'],
                  ['sendable', 'Safe to send'],
                ] as Array<[FilterKey, string]>
              ).map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  size="sm"
                  variant={filter === key ? 'default' : 'outline'}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={selectSendableOnly}>
                Select safe only
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={selectNewOnly}>
                Select new only
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={selectAllVisible}>
                Select all in view
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={clearSelection}>
                Clear
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="p-2 w-10" />
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">History</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const checked = selected.has(row.email.toLowerCase());
                    return (
                      <tr key={row.email} className="border-t">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!row.sendable}
                            onChange={() => toggle(row.email)}
                            aria-label={`Select ${row.email}`}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{row.email}</div>
                          <div className="text-xs text-muted-foreground">{row.name}</div>
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">
                          {row.contactedBefore
                            ? `Last: ${row.lastOutcome || 'sent'}${
                                row.lastSentAt
                                  ? ` · ${new Date(row.lastSentAt).toLocaleDateString()}`
                                  : ''
                              }`
                            : 'New recipient'}
                        </td>
                        <td className="p-2">
                          {row.sendable ? (
                            <Badge variant="outline">OK</Badge>
                          ) : (
                            <span className="text-xs text-destructive">{row.blockReason}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || confirming || selected.size === 0}
            onClick={handleConfirm}
          >
            {confirming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              `Send to ${selected.size} recipient${selected.size === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
